import { apply, celebrate, has, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { layers } from '../ui/layers';
import { DAY_SPAWN } from '../world/map';
import type { MapId } from '../world/mapdef';
import type { World } from '../world/world';
import { startShake } from '../render/weather';
import { isRinging, ringNow } from './calls';
import { logJournal } from './journal';
import { dayNum } from './calendar';
import { isRaining, rainPower, typhoonActive, typhoonDay, typhoonTomorrow, weatherFor } from './weather';

/**
 * Weather consequences and natural events: typhoon days off (颱風假) with a
 * preparation checklist, earthquakes with the "Drop, cover and hold on!"
 * lesson, getting soaked without an umbrella, and Mom's worried phone calls.
 */

let world: World | null = null;
export function bindDisastersWorld(w: World): void {
  world = w;
}

const t = () => m().sky;
const HOME_MAPS = new Set<MapId>(['home', 'balcony']);
/** Places that close their doors while a typhoon passes. */
const CLOSED_IN_TYPHOON = new Set<string>(['school', 'store', 'mall', 'sports', 'market', 'bo', 'station', 'museum', 'mrt', 'taipei', 'hualien', 'grandma', 'ximen', 'longshan', 'cks', 'beitou', 'hlcity']);

/** Door rule (registered by pkgSky): stay indoors while the typhoon passes. */
export function typhoonDoorRule(to: string, from: string): string | null {
  if (!typhoonActive()) return null;
  if (HOME_MAPS.has(from as MapId) && to === 'town') return t().toasts.tooWindy;
  if (CLOSED_IN_TYPHOON.has(to)) return t().toasts.doorClosed;
  return null;
}

// --- Typhoon checklist --------------------------------------------------------------
export const KIT_ITEMS = ['water', 'phone', 'plants', 'flashlight'] as const;
export type KitItem = (typeof KIT_ITEMS)[number];
export const kitDone = (k: KitItem): boolean => !!S().daily[`sky.kit.${k}`];

/** Tick a checklist job; the whole list pays out once. */
export function finishKit(k: KitItem): void {
  const s = S();
  if (kitDone(k)) return;
  s.daily[`sky.kit.${k}`] = true;
  sfx(k === 'water' ? 'water' : k === 'plants' ? 'rustle' : 'pickup');
  apply({ minutes: 10, stars: 2 }, { float: true });
  sayEnglish(t().kit.items[k].en);
  if (KIT_ITEMS.every(kitDone) && onceToday('sky.kit.all')) {
    celebrate(true);
    apply({ stars: 10, mood: 10, love: { mom: 6 } });
    logJournal('🌀', 'sky.typhoonReady');
    toast(`🌀 ${t().kit.all}`);
  }
}

function askPlants(): void {
  say({
    who: t().kit.items.plants.label,
    icon: '🪴',
    text: t().kit.balconyAsk,
    choices: [
      { text: t().kit.balconyYes, cost: '10 min', run: () => (finishKit('plants'), toast(t().kit.balconyDone)) },
      { text: t().kit.balconyNo },
    ],
  });
}

function typhoonMorning(): void {
  const tm = t().typhoonMorning;
  const reply = (good: boolean) => () => {
    toast(good ? tm.goodMsg : tm.badMsg);
    if (good) {
      sfx('ok');
      apply({ love: { mom: 5 }, mood: 3 });
    } else apply({ love: { mom: -3 } }, { float: false });
  };
  const choices: Choice[] = [
    { text: tm.good, run: reply(true) },
    { text: tm.bad, run: reply(false) },
  ];
  if (Math.random() < 0.5) choices.reverse();
  say({ who: tm.who, icon: '👩', text: tm.text, choices });
}

/** Start of a day: typhoon warnings and the day off itself. */
export function disastersDayStart(): void {
  exposure = 0;
  lastMinute = S().time;
  lastMap = null;
  if (typhoonDay()) {
    // Typhoon day off: no school. If the day would start in class, start at home instead.
    const s = S();
    if (s.player.map !== 'home' && world) {
      const h = DAY_SPAWN.home;
      s.player.map = h.map;
      s.player.x = h.x;
      s.player.y = h.y;
      world.teleport(h.map, h.x, h.y);
    }
    window.setTimeout(() => toast(t().toasts.typhoonDay), 1600);
    window.setTimeout(() => (layers.blocking() ? null : typhoonMorning()), 2600);
  } else if (typhoonTomorrow()) {
    window.setTimeout(() => toast(t().toasts.typhoonEve), 2400);
  }
}

// --- Earthquakes ------------------------------------------------------------------
const QUAKE_LAYER = 'sky_quake';
let quaking = false;

function quakeAllowed(w: World): boolean {
  const s = S();
  return (
    !quaking &&
    !layers.blocking() &&
    !isRinging() &&
    w.fade === 0 &&
    !w.inBus &&
    dayNum() >= 3 &&
    !typhoonDay() &&
    s.time > 8 * 60 + 30 &&
    s.time < 21 * 60 &&
    !s.forever[`sky.quake.w${s.week}`]
  );
}

/** Shake the world, then ask what to do. `force` skips the checks (debug). */
export function earthquake(force = false): void {
  const w = world;
  if (!w || quaking || (!force && !quakeAllowed(w))) return;
  const s = S();
  s.forever[`sky.quake.w${s.week}`] = true;
  quaking = true;
  // Freeze the world for the shaking (like any UI layer) — nobody walks during a quake.
  layers.open(QUAKE_LAYER);
  sfx('alarm');
  toast(t().toasts.quakeAlert);
  window.setTimeout(() => {
    sfx('quake');
    startShake(3.2, w.map.indoor ? 3.2 : 2.6);
    for (let i = 0; i < 4; i++) window.setTimeout(() => w.fx.burst('dust', w.player.x + (Math.random() - 0.5) * 80, w.player.y - 20 - Math.random() * 30, 5), 300 + i * 600);
  }, 700);
  window.setTimeout(() => {
    layers.close(QUAKE_LAYER);
    quaking = false;
    safetyQuiz();
  }, 4200);
}

function safetyQuiz(): void {
  const q = t().quake;
  const after = (right: boolean, why: string) => () => {
    if (right) {
      celebrate(true);
      apply({ stars: 6, study: 3 });
      logJournal('🫨', 'sky.quakeSafe');
    } else sfx('bad');
    sayEnglish(q.phrase);
    say({ who: q.who, icon: right ? '✅' : '❌', text: `${why}\n\n${q.lesson}`, choices: [{ text: m().common.ok, run: momAfterQuake }] });
  };
  const choices: Choice[] = [
    { text: q.drop, run: after(true, q.right) },
    { text: q.outside, run: after(false, q.wrongOutside) },
    { text: q.lift, run: after(false, q.wrongLift) },
    { text: q.window, run: after(false, q.wrongWindow) },
  ];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [choices[i], choices[j]] = [choices[j], choices[i]];
  }
  say({ who: q.who, icon: '🫨', text: q.text, choices });
}

function momAfterQuake(): void {
  const q = t().quake;
  if (world && HOME_MAPS.has(world.map.id)) {
    // Mom is right here: no need to phone.
    const reply = (good: boolean) => () => {
      toast(good ? q.momGoodMsg : q.momBadMsg);
      apply(good ? { love: { mom: 8 }, mood: 4 } : { love: { mom: -3 } }, { float: good });
      if (good) sfx('ok');
    };
    say({ who: q.momWho, icon: '👩', text: q.momHome, choices: [{ text: q.momGood, run: reply(true) }, { text: q.momBad, run: reply(false) }] });
  } else window.setTimeout(() => ringNow('momQuake'), 2500);
}

// --- Per-frame rules ----------------------------------------------------------------
let exposure = 0;
let lastMinute = -1;
let lastMap: MapId | null = null;
// Roughly one quake every ~40 real minutes of play, at most one per game week.
const QUAKE_RATE = 1 / 2400;

/** Called every frame while the game runs (from the sky overlay hook). */
export function disastersTick(w: World, dt: number): void {
  const s = S();
  const prevMap = lastMap;
  lastMap = w.map.id;
  const minute = s.time;
  const prevMinute = lastMinute;
  lastMinute = minute;
  if (layers.blocking() || w.fade > 0) return;

  const outdoors = !w.map.indoor;
  const raining = isRaining();

  // Rain: a heads-up, the umbrella, or getting soaked (once a day).
  if (outdoors && raining) {
    if (has('umbrella')) {
      if (onceToday('sky.umbrellaOpen')) {
        sfx('umbrella');
        toast(t().toasts.umbrellaOpen);
      }
    } else {
      const kind = weatherFor(dayNum()).kind;
      if (onceToday('sky.rainToast')) toast(kind === 'typhoon' ? t().toasts.windy : kind === 'storm' ? t().toasts.stormStarts : t().toasts.rainStarts);
      exposure += dt * Math.min(1, rainPower() * 1.6);
      if (exposure > 7 && onceToday('sky.soaked')) {
        sfx('bad');
        w.fx.burst('drop', w.player.x, w.player.y - 26, 8);
        apply({ mood: -8, energy: -6 }, { float: false });
        toast(t().toasts.soaked);
      }
    }
    // Mom checks you took an umbrella the first time it rains while you're out.
    if (!HOME_MAPS.has(w.map.id) && !s.forever['sky.momRain']) {
      s.forever['sky.momRain'] = true;
      window.setTimeout(() => ringNow('momRain'), 3000);
    }
  }

  // Typhoon eve: Mom calls when you're out; a reminder at dinner time.
  const tomorrow = dayNum() + 1;
  if (typhoonTomorrow()) {
    if (minute >= 10 * 60 && !HOME_MAPS.has(w.map.id) && !s.forever[`sky.momTyphoon.${tomorrow}`]) {
      s.forever[`sky.momTyphoon.${tomorrow}`] = true;
      ringNow('momTyphoon');
    }
    if (minute >= 18 * 60 && onceToday('sky.typhoonEve18')) toast(t().toasts.typhoonEve);
  }

  if (typhoonDay()) {
    // Out in the typhoon (after it eases, or on the balcony): Mom worries.
    if (!HOME_MAPS.has(w.map.id) && !s.forever[`sky.momTyphoon.${dayNum()}`]) {
      s.forever[`sky.momTyphoon.${dayNum()}`] = true;
      ringNow('momTyphoon');
    }
    if (typhoonActive()) {
      if (w.map.id === 'balcony' && prevMap !== 'balcony' && prevMap !== null) {
        if (!kitDone('plants')) askPlants();
        else if (onceToday('sky.windyBalcony')) toast(t().toasts.windy);
      }
    } else if (prevMinute >= 0 && onceToday('sky.typhoonPassed')) toast(t().toasts.typhoonPassed);
  }

  // Earthquakes: rare and random.
  if (Math.random() < dt * QUAKE_RATE && quakeAllowed(w)) earthquake();
}
